import { Injectable } from '@angular/core'
import { AnnotationsApiClient, UpdateCaseRequest } from '@ftr/annotations-contracts'
import { AnnotationsApiClientFactory, CasesService } from '@ftr/api-annotations'
import { NotFoundApiError } from '@ftr/api-shared'
import { Uuid } from '@ftr/contracts/type/shared/Uuid'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { RemoteData } from '@ftr/foundation'
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store'
import { patch, ɵPatchSpec } from '@ngxs/store/operators'
import compact from 'lodash/compact'
import flatMap from 'lodash/flatMap'
import memoize from 'lodash/memoize'
import uniqBy from 'lodash/uniqBy'
import { firstValueFrom, lastValueFrom, of } from 'rxjs'
import {
  DeleteCaseAction,
  FetchCaseAction,
  FetchCaseDetailsAction,
  FetchHearingSectionsListAction,
  FetchSelectedHearingSectionAction,
  InitializeCaseDetailsAction,
  InitializeHearingSectionsListAction,
  SelectHearingSectionAction,
  SetCaseRemoteAction,
  UpdateCaseAction,
  UpdateCaseDetailsAction,
} from './cases.actions'
import { CaseDeletedEvent, CaseDeletionFailedEvent, CaseUpdateFailedEvent, CaseUpdatedEvent } from './cases.events'
import {
  CaseDetailsModel,
  CaseInstanceStateModel,
  CasesStateModel,
  HearingSectionListModel,
  HearingSectionsListStateModel,
  SelectedHearingSectionStateModel,
} from './cases.model'

export function defaultCasesState(): CasesStateModel {
  return {
    caseInstanceState: {},
    caseDetails: {},
  }
}

function defaultCaseInstanceState(): CaseInstanceStateModel {
  return {
    caseRemote: RemoteData.notAsked(),
    expires: Date.now(),
  }
}

function defaultCaseDetailsInstanceState(): CaseDetailsModel {
  return {
    state: 'init',
  }
}

function defaultHearingSectionsListInstanceState(): HearingSectionsListStateModel {
  return {
    state: 'init',
  }
}

function defaultSelectedHearingSectionInstanceState(): SelectedHearingSectionStateModel {
  return {
    state: 'init',
  }
}

@State<CasesStateModel>({
  name: 'casesState',
  defaults: defaultCasesState(),
})
@Injectable()
export class CasesState {
  constructor(
    private readonly regionCache: CourtSystemRegionCache,
    private readonly annotationsApiFactory: AnnotationsApiClientFactory,
    private readonly casesService: CasesService,
  ) {}

  @Selector()
  static allCaseInstanceStates(state: CasesStateModel): CasesStateModel['caseInstanceState'] {
    return state.caseInstanceState
  }

  @Selector()
  static allCaseDetailsInstanceStates(state: CasesStateModel): CasesStateModel['caseDetails'] {
    return state.caseDetails
  }

  static readonly caseInstanceState = memoize(
    (caseId: Uuid): ((sourceStates: ReturnType<typeof CasesState.allCaseInstanceStates>) => CaseInstanceStateModel) => {
      return createSelector(
        [CasesState.allCaseInstanceStates],
        (sourceStates: ReturnType<typeof CasesState.allCaseInstanceStates>) =>
          sourceStates[caseId] ?? defaultCaseInstanceState(),
      )
    },
  )

  static readonly caseDetailsInstanceState = memoize(
    (
      caseId: Uuid,
    ): ((sourceStates: ReturnType<typeof CasesState.allCaseDetailsInstanceStates>) => CaseDetailsModel) => {
      return createSelector(
        [CasesState.allCaseDetailsInstanceStates],
        (sourceStates: ReturnType<typeof CasesState.allCaseDetailsInstanceStates>) =>
          sourceStates[caseId] ?? defaultCaseDetailsInstanceState(),
      )
    },
  )

  static readonly caseDetailsHearingSectionInstanceState = memoize(
    (
      caseId: Uuid,
      hearingSectionId: Uuid,
    ): ((
      sourceStates: ReturnType<typeof CasesState.allCaseDetailsInstanceStates>,
    ) => HearingSectionListModel['hearingSections'][0] | undefined) => {
      return createSelector(
        [CasesState.allCaseDetailsInstanceStates],
        (sourceStates: ReturnType<typeof CasesState.allCaseDetailsInstanceStates>) => {
          const caseDetailsInstanceState = CasesState.caseDetailsInstanceState(caseId)(sourceStates)

          if (caseDetailsInstanceState.state === 'idle') {
            const hearingSectionsList = caseDetailsInstanceState.hearingSectionsList
            if (hearingSectionsList.state === 'idle') {
              return hearingSectionsList.hearingSections.find(hs => hs.id === hearingSectionId)
            }
          }
          return undefined
        },
      )
    },
  )

  @Action(FetchCaseAction)
  async fetchCase(
    { getState, dispatch }: StateContext<CasesStateModel>,
    { courtSystemId, caseId }: FetchCaseAction,
  ): Promise<void> {
    const originalState = getState().caseInstanceState[caseId] ?? defaultCaseInstanceState()

    // Don't re-fetch the active case if there is already a result or request in flight
    // Clear the state first if you want to re-fetch
    const existingFetch = originalState.caseRemote

    const expired = Date.now() > originalState.expires

    if (existingFetch.isLoading() || (existingFetch.isSuccess() && !expired)) {
      // Do nothing if it is already loading or has a successful result which is not expired
      return
    }

    dispatch(new SetCaseRemoteAction(caseId, RemoteData.loading()))

    runWithoutWaitingForActionToEnd(async () => {
      const apiClient = this.annotationsApiFactory.build(
        (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(courtSystemId)))).region,
      )
      const result = await apiClient.case.getCase({ params: { caseId } }).catch(err => ({ fetchError: err }))
      if ('fetchError' in result) {
        dispatch(new SetCaseRemoteAction(caseId, RemoteData.failure(result.fetchError as Error)))
      } else if (result.status !== 200) {
        dispatch(
          new SetCaseRemoteAction(
            caseId,
            RemoteData.failure(result.status === 404 ? new NotFoundApiError() : new Error(result.body.error)),
          ),
        )
      } else {
        dispatch(new SetCaseRemoteAction(caseId, RemoteData.success(result.body)))
      }
    })
  }

  @Action(InitializeCaseDetailsAction)
  async initializeCaseDetails(
    { getState, dispatch }: StateContext<CasesStateModel>,
    { courtSystemId, caseId }: InitializeCaseDetailsAction,
  ): Promise<void> {
    const originalState = getState().caseDetails[caseId] ?? defaultCaseDetailsInstanceState()

    if (
      originalState.state === 'loading' ||
      (originalState.state === 'idle' && originalState.caseRemote.isSuccess() && Date.now() < originalState.expires)
    ) {
      // Do nothing if it is already loading or has a successful result which is not expired
      return
    }
    dispatch(new FetchCaseDetailsAction(courtSystemId, caseId))
  }

  @Action(FetchCaseDetailsAction)
  async fetchCaseDetails(
    { setState, dispatch }: StateContext<CasesStateModel>,
    { courtSystemId, caseId }: FetchCaseDetailsAction,
  ): Promise<void> {
    setState(
      patch({
        caseDetails: patch({
          [caseId]: patch({
            state: 'loading',
            hearingSectionsList: defaultHearingSectionsListInstanceState(),
            selectedHearingSection: defaultSelectedHearingSectionInstanceState(),
            caseRemote: RemoteData.loading(),
          }),
        }),
      }),
    )

    runWithoutWaitingForActionToEnd(async () => {
      try {
        const result = await lastValueFrom(this.casesService.getCase({ caseId, courtSystemId }))

        if (result.isFailure()) {
          throw new Error(result._error.message ?? 'Unknown error')
        }

        dispatch(new InitializeHearingSectionsListAction(courtSystemId, caseId))

        if (result.isSuccess()) {
          setState(
            patch({
              caseDetails: patch({
                [caseId]: patch({
                  state: 'idle',
                  expires: Date.now() + 5000,
                  case: result._data,
                  caseJudges: [],
                  caseRemote: result,
                  saveStatus: 'loaded',
                }),
              }),
            }),
          )
        }
      } catch (err) {
        setState(
          patch({
            caseDetails: patch({
              [caseId]: patch({
                state: 'failure',
                error: err as Error,
              }),
            }),
          }),
        )
      }
    })
  }

  @Action(InitializeHearingSectionsListAction)
  async initializeHearingSectionsList(
    { getState, dispatch }: StateContext<CasesStateModel>,
    { courtSystemId, caseId }: InitializeHearingSectionsListAction,
  ): Promise<void> {
    const originalState = getState().caseDetails[caseId] ?? defaultCaseDetailsInstanceState()

    if (originalState.state === 'loading') {
      // If we're already loading the case, kick off the fetch
      dispatch(new FetchHearingSectionsListAction(courtSystemId, caseId))
    }

    // If we're not idling on the case, do nothing
    if (originalState.state !== 'idle') {
      return
    }

    const originalHearingSectionsListState = originalState.hearingSectionsList
    if (
      originalHearingSectionsListState.state === 'loading' ||
      (originalHearingSectionsListState.state === 'idle' &&
        originalHearingSectionsListState.hearingSectionsRemote.isSuccess() &&
        Date.now() < originalHearingSectionsListState.expires)
    ) {
      // Do nothing if it is already loading or has a successful result which is not expired
      return
    }

    dispatch(new FetchHearingSectionsListAction(courtSystemId, caseId))
  }

  @Action(FetchHearingSectionsListAction)
  async fetchHearingSectionsList(
    { setState, dispatch }: StateContext<CasesStateModel>,
    { courtSystemId, caseId }: FetchHearingSectionsListAction,
  ): Promise<void> {
    setState(
      patch({
        caseDetails: patch({
          [caseId]: patch({
            hearingSectionsList: { state: 'loading', hearingSectionsRemote: RemoteData.loading() },
          }),
        }),
      }),
    )

    runWithoutWaitingForActionToEnd(async () => {
      const apiClient = this.annotationsApiFactory.build(
        (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(courtSystemId)))).region,
      )

      try {
        const result = await apiClient.case.getHearingsForCase({
          query: { limit: 100, offset: 0, sortDirection: 'asc' },
          params: { caseId },
        })
        if (result.status !== 200) {
          throw new Error(result.body?.error ?? 'Unknown error')
        }

        const allHearingSections = flatMap(result.body.items, x => x.hearingSections)

        const selectedHearingSection = allHearingSections.length > 0 ? allHearingSections[0] : undefined
        dispatch(
          new SelectHearingSectionAction(courtSystemId, caseId, selectedHearingSection?.id, selectedHearingSection),
        )

        const caseJudges = uniqBy(
          flatMap(result.body.items, x => x.judgesPresiding),
          x => x.judgeId,
        )

        setState(
          patch({
            caseDetails: patch({
              [caseId]: patch({
                caseJudges,
                hearingSectionsList: {
                  state: 'idle',
                  expires: Date.now() + 5000,
                  hearingSections: allHearingSections,
                  hearingSectionsRemote: RemoteData.success(allHearingSections),
                },
              }),
            }),
          }),
        )
      } catch (err) {
        setState(
          patch({
            caseDetails: patch({
              [caseId]: patch({
                hearingSectionsList: { state: 'failure', error: err as Error },
              }),
            }),
          }),
        )
      }
    })
  }

  @Action(SelectHearingSectionAction)
  async selectHearingSection(
    { getState, setState, dispatch }: StateContext<CasesStateModel>,
    { courtSystemId, caseId, hearingSectionId, hearingSection }: SelectHearingSectionAction,
  ): Promise<void> {
    if (!hearingSectionId) {
      setState(
        patch({
          caseDetails: patch({
            [caseId]: patch({
              selectedHearingSection: { state: 'empty' },
            }),
          }),
        }),
      )
      return
    }

    const originalState = getState().caseDetails[caseId] ?? defaultCaseDetailsInstanceState()

    if (originalState.state === 'loading') {
      // If we're already loading the case, kick off the fetch
      dispatch(new FetchSelectedHearingSectionAction(courtSystemId, caseId, hearingSectionId, hearingSection))
    }

    // If we're not idling on the case, then something is wrong and we should do nothing
    if (originalState.state !== 'idle') {
      return
    }

    const originalSelectedHearingSectionState = originalState.selectedHearingSection
    if (
      originalSelectedHearingSectionState.state === 'loading' ||
      (originalSelectedHearingSectionState.state === 'idle' &&
        originalSelectedHearingSectionState.hearingSectionRemote.isSuccess() &&
        originalSelectedHearingSectionState.hearingSection.id === hearingSectionId &&
        Date.now() < originalSelectedHearingSectionState.expires)
    ) {
      return
    }

    dispatch(new FetchSelectedHearingSectionAction(courtSystemId, caseId, hearingSectionId, hearingSection))
  }

  @Action(FetchSelectedHearingSectionAction)
  async fetchSelectedHearingSection(
    { setState }: StateContext<CasesStateModel>,
    { courtSystemId, caseId, hearingSectionId, hearingSection }: FetchSelectedHearingSectionAction,
  ): Promise<void> {
    setState(
      patch({
        caseDetails: patch({
          [caseId]: patch({
            selectedHearingSection: {
              state: 'loading',
              hearingSection: hearingSection ?? {},
              hearingSectionRemote: RemoteData.loading(),
            },
          }),
        }),
      }),
    )

    runWithoutWaitingForActionToEnd(async () => {
      const _apiClient = this.annotationsApiFactory.build(
        (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(courtSystemId)))).region,
      )

      try {
        // TODO: we don't have a fetch hearing section by id endpoint (at least not one that can be called from the web)
        //  So we're going to assume the hearing section has been passed in with the action and fake the load
        if (!hearingSection) {
          throw new Error('Hearing section not found')
        }

        const result: Awaited<ReturnType<typeof _apiClient.hearingSection.getHearingSection>> = await Promise.resolve({
          status: hearingSection ? 200 : 404,
          body: hearingSection ? { ...hearingSection, id: hearingSectionId } : null,
        } as unknown as Awaited<ReturnType<typeof _apiClient.hearingSection.getHearingSection>>)

        if (result.status !== 200) {
          throw new Error(result.body?.error ?? 'Unknown error')
        }

        setState(
          patch({
            caseDetails: patch({
              [caseId]: patch({
                selectedHearingSection: {
                  state: 'idle',
                  expires: Date.now() + 5000,
                  hearingSection: result.body,
                  hearingSectionRemote: RemoteData.success(result.body),
                },
              }),
            }),
          }),
        )
      } catch (err) {
        setState(
          patch({
            caseDetails: patch({
              [caseId]: patch({
                selectedHearingSection: { state: 'failure', error: err as Error },
              }),
            }),
          }),
        )
      }
    })
  }

  private async updateCaseShared({
    courtSystemId,
    caseId,
    caseUpdate,
  }: {
    courtSystemId: Uuid
    caseId: Uuid
    caseUpdate: UpdateCaseRequest
  }): Promise<ReturnType<AnnotationsApiClient['case']['updateCase']>> {
    const groomedCaseUpdate = {
      ...caseUpdate,
      title: caseUpdate.title?.trim() || null,
    }
    const apiClient = this.annotationsApiFactory.build(
      (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(courtSystemId)))).region,
    )

    const result = await apiClient.case.updateCase({
      body: groomedCaseUpdate,
      params: { caseId },
    })

    return result
  }

  @Action(UpdateCaseDetailsAction)
  async updateCaseDetails(
    { getState, setState, dispatch }: StateContext<CasesStateModel>,
    { caseId, caseUpdate }: UpdateCaseDetailsAction,
  ): Promise<void> {
    const originalState = CasesState.caseDetailsInstanceState(caseId)(getState().caseDetails)

    if (originalState.state !== 'idle') {
      // Do nothing if the case details are not idle
      return
    }

    const failCurrentUpdateRequest = (reason: 'unknown' | 'conflict'): void => {
      const changedFields = originalState.caseRemote.isSuccess()
        ? compact([
            originalState.caseRemote._data.title !== caseUpdate.title ? 'title' : undefined,
            originalState.caseRemote._data.caseReference !== caseUpdate.caseReference ? 'caseReference' : undefined,
          ])
        : []

      setState(
        patch({
          caseDetails: patch({
            [caseId]: patch({
              saveStatus: {
                errorMessage: reason,
                showRetry: false,
                showReset: true,
                fields: changedFields,
              },
            }),
          }),
        }),
      )
    }

    try {
      const updatedCase = {
        ...originalState.case,
        ...caseUpdate,
      }
      // Update the local case details to the new values
      setState(
        patch({
          caseDetails: patch({
            [caseId]: patch({
              case: updatedCase,
              saveStatus: 'saving',
            }),
          }),
        }),
      )
      // Update the case details on the server
      const result = await this.updateCaseShared({
        courtSystemId: originalState.case.courtSystemId,
        caseId,
        caseUpdate,
      })

      // If the update failed, fail the current update request
      if (result.status !== 200) {
        return failCurrentUpdateRequest(result.status === 409 ? 'conflict' : 'unknown')
      }

      // Otherwise, mark the update as successful
      const latestCase = {
        ...originalState.case,
        ...result.body,
      }
      setState(
        patch({
          caseDetails: patch({
            [caseId]: patch({
              case: latestCase,
              caseRemote: RemoteData.success(latestCase),
              saveStatus: 'saved',
            }),
          }),
        }),
      )
    } catch {
      // If the update failed, fail the current update request
      failCurrentUpdateRequest('unknown')
      // Dispatch an event to the rest of the application that the update failed
      dispatch(new CaseUpdateFailedEvent(caseId, 'unknown'))
    }
  }

  @Action(UpdateCaseAction)
  async updateCase(
    { getState, dispatch }: StateContext<CasesStateModel>,
    { caseId, caseUpdate }: UpdateCaseAction,
  ): Promise<void> {
    const originalState = getState().caseInstanceState[caseId] ?? defaultCaseInstanceState()
    if (!originalState.caseRemote.isSuccess()) {
      dispatch(new CaseUpdateFailedEvent(caseId, 'unknown'))
      return
    }

    try {
      const result = await this.updateCaseShared({
        courtSystemId: originalState.caseRemote._data.courtSystemId,
        caseId,
        caseUpdate,
      })

      if (result.status !== 200) {
        dispatch(new CaseUpdateFailedEvent(caseId, result.status === 409 ? 'conflict' : 'unknown'))
        return
      }

      dispatch([
        new SetCaseRemoteAction(caseId, RemoteData.success(result.body)),
        new CaseUpdatedEvent(caseId, originalState.caseRemote._data, result.body),
      ])
    } catch {
      dispatch(new CaseUpdateFailedEvent(caseId, 'unknown'))
    }
  }

  @Action(DeleteCaseAction)
  async deleteCase({ getState, dispatch }: StateContext<CasesStateModel>, { caseId }: DeleteCaseAction): Promise<void> {
    const originalState = getState().caseInstanceState[caseId] ?? defaultCaseInstanceState()

    if (!originalState.caseRemote.isSuccess()) {
      dispatch(new CaseDeletionFailedEvent(caseId))
      return
    }

    const apiClient = this.annotationsApiFactory.build(
      (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(originalState.caseRemote._data.courtSystemId))))
        .region,
    )

    const result = await apiClient.case
      .deleteCase({
        params: { caseId },
      })
      .catch(err => ({ fetchError: err }))
    if ('fetchError' in result) {
      dispatch(new CaseDeletionFailedEvent(caseId))
      return
    }

    if (result.status !== 204) {
      dispatch(new CaseDeletionFailedEvent(caseId))
      return
    }

    dispatch([new SetCaseRemoteAction(caseId, RemoteData.notAsked()), new CaseDeletedEvent(caseId)])
  }

  @Action(SetCaseRemoteAction)
  async setCase(
    { setState }: StateContext<CasesStateModel>,
    { caseId, caseRemote }: SetCaseRemoteAction,
  ): Promise<void> {
    patchCaseInstanceState(setState, caseId, {
      caseRemote,
      expires: Date.now() + 5000,
    })
  }
}

function patchCaseInstanceState(
  setState: StateContext<CasesStateModel>['setState'],
  caseId: Uuid,
  update: ɵPatchSpec<CaseInstanceStateModel>,
): void {
  setState(
    patch<CasesStateModel>({
      caseInstanceState: patch({
        [caseId]: patch(update),
      }),
    }),
  )
}

function runWithoutWaitingForActionToEnd(callback: () => Promise<void>): void {
  void callback()
}
